import React, { useMemo } from 'react';
import {
  useReports,
  useAppStore,
  useTimerStore,
  formatDuration,
  localDateKey,
} from '@flowfocus/core';
import { Segmented, Stepper } from '../components/ui';
import { Clock, Flame, TrendingUp, CheckCircle2, Target } from 'lucide-react';

/* ── helpers ─────────────────────────────────────────────────────────── */

const todayKey = (): string => localDateKey();

function computeStreak(dates: Set<string>): number {
  let streak = 0;
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const key = localDateKey;
  if (!dates.has(key(d))) d.setDate(d.getDate() - 1);
  while (dates.has(key(d))) {
    streak++;
    d.setDate(d.getDate() - 1);
  }
  return streak;
}

/* ── small metric card ───────────────────────────────────────────────── */

function MetricCard({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
}) {
  return (
    <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          background: 'var(--primary-soft)',
          color: 'var(--primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-pri)', lineHeight: 1.1 }}>
          {value}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-mut)', marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

/* ── circular goal ring ──────────────────────────────────────────────── */

function GoalRing({ value, goal }: { value: number; goal: number }) {
  const size = 132;
  const stroke = 11;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = goal > 0 ? Math.min(1, value / goal) : 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--fill)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--primary)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 500ms ease' }}
      />
      <text
        x="50%"
        y="46%"
        textAnchor="middle"
        fontSize={28}
        fontWeight={700}
        fill="var(--text-pri)"
      >
        {value}
      </text>
      <text
        x="50%"
        y="64%"
        textAnchor="middle"
        fontSize={12}
        fill="var(--text-mut)"
      >
        / {goal}
      </text>
    </svg>
  );
}

/* ── page ────────────────────────────────────────────────────────────── */

export function StatsPage({ uid }: { uid: string }) {
  const r = useReports(uid);
  const tasks = useAppStore((s) => s.tasks);
  const projects = useAppStore((s) => s.projects);
  const settings = useTimerStore((s) => s.settings);
  const updateSettings = useTimerStore((s) => s.updateSettings);

  const tk = todayKey();

  const dailyByDate = useMemo(() => {
    const m = new Map<string, (typeof r.daily)[number]>();
    for (const d of r.daily) m.set(d.date, d);
    return m;
  }, [r.daily]);

  // racha y mapa de calor: últimos 90 días, no solo el rango elegido (antes con "Semana" la racha nunca pasaba de 7)
  const historyByDate = useMemo(() => {
    const m = new Map<string, (typeof r.history)[number]>();
    for (const d of r.history) m.set(d.date, d);
    return m;
  }, [r.history]);

  const streak = useMemo(
    () => computeStreak(new Set(r.history.filter((x) => x.sessions > 0).map((x) => x.date))),
    [r.history]
  );

  const completedTasks = useMemo(() => tasks.filter((t) => t.completed).length, [tasks]);

  const todaySessions = historyByDate.get(tk)?.sessions ?? dailyByDate.get(tk)?.sessions ?? 0;
  const goal = settings.dailyGoal;

  /* last 14 days for the bar chart */
  const last14 = r.daily.slice(-14);
  const barMax = Math.max(1, ...last14.map((d) => d.focusSeconds));

  /* by project (top 6) */
  const byProject = useMemo(() => [...r.byProject].sort((a, b) => b.seconds - a.seconds).slice(0, 6), [r.byProject]);
  const projectTotal = byProject.reduce((a, b) => a + b.seconds, 0);

  /* heatmap: last 12 weeks (7 rows x 12 cols), ending today */
  const heat = useMemo(() => {
    const WEEKS = 12;
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    // move to the end of the current week (Sunday-based grid, Monday first row)
    const dow = (end.getDay() + 6) % 7; // 0 = Monday
    const gridEnd = new Date(end);
    gridEnd.setDate(end.getDate() + (6 - dow));
    const cols: { date: string; sessions: number; future: boolean }[][] = [];
    let maxSessions = 0;
    for (const d of r.history) maxSessions = Math.max(maxSessions, d.sessions);
    for (let w = WEEKS - 1; w >= 0; w--) {
      const col: { date: string; sessions: number; future: boolean }[] = [];
      for (let row = 0; row < 7; row++) {
        const cell = new Date(gridEnd);
        cell.setDate(gridEnd.getDate() - w * 7 - (6 - row));
        const key = localDateKey(cell);
        col.push({
          date: key,
          sessions: historyByDate.get(key)?.sessions ?? 0,
          future: cell.getTime() > end.getTime(),
        });
      }
      cols.push(col);
    }
    return { cols, maxSessions: Math.max(1, maxSessions) };
  }, [r.history, historyByDate]);

  const cardTitle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-sec)',
    margin: '0 0 14px',
  };

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '22px 24px 80px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 20,
        }}
      >
        <h1
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: 'var(--text-pri)',
            margin: 0,
            letterSpacing: '-0.5px',
          }}
        >
          Estadísticas
        </h1>
        <Segmented<typeof r.range>
          value={r.range}
          onChange={r.setRange}
          options={[
            { id: 'day', label: 'Hoy' },
            { id: 'week', label: 'Semana' },
            { id: 'month', label: 'Mes' },
            { id: 'year', label: 'Año' },
          ]}
        />
      </div>

      {/* Metric cards */}
      <div
        className="metrics-grid"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}
      >
        <MetricCard icon={<Clock size={18} />} value={r.totalFocusFormatted} label="Tiempo enfocado" />
        <MetricCard icon={<Flame size={18} />} value={r.totalSessions} label="Pomodoros" />
        <MetricCard icon={<TrendingUp size={18} />} value={streak} label="Racha de días" />
        <MetricCard icon={<CheckCircle2 size={18} />} value={completedTasks} label="Tareas completadas" />
      </div>

      <div className="reports-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Objetivo diario */}
        <div className="card" style={{ padding: 20 }}>
          <h3 style={cardTitle}>
            <Target size={14} style={{ verticalAlign: '-2px', marginRight: 6, color: 'var(--primary)' }} />
            Objetivo diario
          </h3>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 20,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <GoalRing value={todaySessions} goal={goal} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
              <div style={{ fontSize: 13, color: 'var(--text-sec)' }}>
                <strong style={{ color: 'var(--text-pri)' }}>{todaySessions}</strong> / {goal} pomodoros
              </div>
              <Stepper
                value={goal}
                min={1}
                max={20}
                onChange={(v) => updateSettings({ dailyGoal: v })}
              />
              <span style={{ fontSize: 11, color: 'var(--text-mut)' }}>Meta diaria</span>
            </div>
          </div>
        </div>

        {/* Enfoque por día */}
        <div className="card" style={{ padding: 20 }}>
          <h3 style={cardTitle}>Enfoque por día</h3>
          {last14.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--text-mut)', textAlign: 'center', padding: '24px 0' }}>
              Sin datos para este período
            </p>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 130, paddingTop: 6, minWidth: 0 }}>
              {last14.map((d) => {
                const pct = Math.max(4, Math.round((d.focusSeconds / barMax) * 100));
                return (
                  <div key={d.date} title={`${d.date} · ${formatDuration(d.focusSeconds)}`}
                    style={{ flex: '1 1 0', minWidth: 0, maxWidth: 26, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%' }}>
                    <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
                      <div style={{ width: '100%', height: `${pct}%`, borderRadius: 5, background: 'var(--primary)' }} />
                    </div>
                    <span style={{ fontSize: 9, color: 'var(--text-mut)' }}>{Number(d.date.slice(8, 10))}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Por proyecto */}
        <div className="card" style={{ padding: 20 }}>
          <h3 style={cardTitle}>Por proyecto</h3>
          {byProject.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--text-mut)', textAlign: 'center', padding: '24px 0' }}>
              Sin datos para este período
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {byProject.map((bp) => {
                const proj = projects.find((p) => p.id === bp.projectId);
                const name = proj?.name ?? 'Proyecto';
                const color = proj?.color ?? 'var(--primary)';
                const pct = projectTotal > 0 ? (bp.seconds / projectTotal) * 100 : 0;
                return (
                  <div key={bp.projectId} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span
                        style={{
                          fontSize: 12,
                          color: 'var(--text-sec)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '60%',
                        }}
                      >
                        {name}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-pri)' }}>
                        {formatDuration(bp.seconds)}
                      </span>
                    </div>
                    <div
                      style={{
                        height: 8,
                        borderRadius: 40,
                        background: 'var(--fill)',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${pct}%`,
                          background: color,
                          borderRadius: 40,
                          transition: 'width 500ms ease',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Mapa de calor */}
        <div className="card" style={{ padding: 20 }}>
          <h3 style={cardTitle}>Mapa de calor</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* weekday labels */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                fontSize: 9,
                color: 'var(--text-mut)',
                paddingTop: 1,
              }}
            >
              {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((wd, i) => (
                <div key={i} style={{ height: 14, lineHeight: '14px' }}>
                  {i % 2 === 0 ? wd : ''}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 4, overflowX: 'auto' }}>
              {heat.cols.map((col, ci) => (
                <div key={ci} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {col.map((cell, ri) => {
                    const intensity = cell.sessions === 0 ? 0 : Math.min(1, cell.sessions / heat.maxSessions);
                    return (
                      <div
                        key={ri}
                        title={cell.future ? undefined : `${cell.date} · ${cell.sessions} pomodoros`}
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 3,
                          background:
                            cell.future || cell.sessions === 0 ? 'var(--fill)' : 'var(--primary)',
                          opacity: cell.future ? 0.4 : cell.sessions === 0 ? 1 : 0.25 + intensity * 0.75,
                        }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 5,
              marginTop: 12,
              fontSize: 10,
              color: 'var(--text-mut)',
            }}
          >
            <span>Menos</span>
            {[0, 0.25, 0.5, 0.75, 1].map((o, i) => (
              <div
                key={i}
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  background: o === 0 ? 'var(--fill)' : 'var(--primary)',
                  opacity: o === 0 ? 1 : 0.25 + o * 0.75,
                }}
              />
            ))}
            <span>Más</span>
          </div>
        </div>
      </div>
    </div>
  );
}
